import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { getLaboratories, createLaboratory, updateLaboratory, deleteLaboratory, getUsers } from '../api';

function Laboratories({ user, onLogout }) {
  const [laboratories, setLaboratories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    institution: '',
    address: '',
    city: '',
    country: '',
    director: '',
    email: '',
    phone: '',
    website: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    fetchLaboratories();
    fetchUsers();
  }, []);

  const fetchLaboratories = async () => {
    try {
      setLoading(true);
      const response = await getLaboratories();
      setLaboratories(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching laboratories:', err);
      setError('Failed to load laboratories');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingLab) {
        await updateLaboratory(editingLab._id, formData);
        setSuccess('Laboratory updated successfully');
      } else {
        await createLaboratory(formData);
        setSuccess('Laboratory created successfully');
      }
      
      fetchLaboratories();
      fetchUsers(); // Refresh users list to get updated roles
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving laboratory:', err);
      setError(err.response?.data?.error || 'Failed to save laboratory');
    }
  };

  const handleEdit = (lab) => {
    setEditingLab(lab);
    setFormData({
      name: lab.name,
      code: lab.code,
      institution: lab.institution,
      address: lab.address || '',
      city: lab.city || '',
      country: lab.country || '',
      director: lab.director ? lab.director._id : '',
      email: lab.email || '',
      phone: lab.phone || '',
      website: lab.website || '',
      description: lab.description || '',
      status: lab.status
    });
    setShowForm(true);
  };

  const handleDelete = async (labId) => {
    if (!window.confirm('Are you sure you want to delete this laboratory?')) {
      return;
    }

    try {
      await deleteLaboratory(labId);
      setSuccess('Laboratory deleted successfully');
      fetchLaboratories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting laboratory:', err);
      setError('Failed to delete laboratory');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      institution: '',
      address: '',
      city: '',
      country: '',
      director: '',
      email: '',
      phone: '',
      website: '',
      description: '',
      status: 'active'
    });
    setEditingLab(null);
    setShowForm(false);
  };

  return (
    <div className="dashboard-container">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="main-content">
        <div className="page-header">
          <h1>Laboratories</h1>
          <p>Manage laboratory information</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="content-section">
          <div className="section-header">
            <h2>All Laboratories</h2>
            <button 
              className="btn btn-success"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              + New Laboratory
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading laboratories...</div>
          ) : laboratories.length === 0 ? (
            <div className="empty-state">
              <p>No laboratories yet</p>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                Create your first laboratory
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Institution</th>
                    <th>Director</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {laboratories.map((lab) => (
                    <tr key={lab._id}>
                      <td data-label="Name">{lab.name}</td>
                      <td data-label="Code">{lab.code}</td>
                      <td data-label="Institution">{lab.institution || '-'}</td>
                      <td data-label="Director">
                        {lab.director ? (lab.director.full_name || lab.director.username) : '-'}
                      </td>
                      <td data-label="Status">
                        <span className={`status-badge status-${lab.status}`}>
                          {lab.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-primary btn-small"
                            onClick={() => handleEdit(lab)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-small"
                            onClick={() => handleDelete(lab._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Edit/Create Laboratory Modal */}
        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingLab ? 'Edit Laboratory' : 'New Laboratory'}</h2>
                <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                      <div className="form-group">
                        <label>Laboratory Name *</label>
                        <input
                          type="text"
                          name="name"
                          className="form-control"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Laboratory Code *</label>
                        <input
                          type="text"
                          name="code"
                          className="form-control"
                          value={formData.code}
                          onChange={handleInputChange}
                          placeholder="e.g., LAB001"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Institution *</label>
                        <input
                          type="text"
                          name="institution"
                          className="form-control"
                          value={formData.institution}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      {editingLab && (
                        <div className="form-group">
                          <label>Directors (Laboratory Managers)</label>
                          <div className="form-control-static">
                            {(() => {
                              const directors = users.filter(u => {
                                const userLabId = typeof u.laboratory === 'object' ? u.laboratory?._id : u.laboratory;
                                return userLabId === editingLab._id && u.role === 'director';
                              });
                              
                              if (directors.length === 0) {
                                return 'No directors assigned';
                              }
                              
                              return (
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                  {directors.map(director => (
                                    <li key={director._id}>
                                      {director.full_name || director.username} ({director.email})
                                    </li>
                                  ))}
                                </ul>
                              );
                            })()}
                          </div>
                          <small className="form-text">
                            To add/remove directors, go to Users page and assign director role to users in this lab
                          </small>
                        </div>
                      )}

                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Address</label>
                        <input
                          type="text"
                          name="address"
                          className="form-control"
                          value={formData.address}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          name="city"
                          className="form-control"
                          value={formData.city}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Country</label>
                        <input
                          type="text"
                          name="country"
                          className="form-control"
                          value={formData.country}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Website</label>
                        <input
                          type="url"
                          name="website"
                          className="form-control"
                          value={formData.website}
                          onChange={handleInputChange}
                          placeholder="https://..."
                        />
                      </div>

                      <div className="form-group">
                        <label>Status</label>
                        <select
                          name="status"
                          className="form-control"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          name="description"
                          className="form-control"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="4"
                          placeholder="Enter laboratory description..."
                        />
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => setShowForm(false)}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-success">
                        {editingLab ? 'Update Laboratory' : 'Create Laboratory'}
                      </button>
                    </div>
                  </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Laboratories;
